// File upload widget for the WebUI MVP.
//
// Accepts .json / .csv / .xlsx files (Ant Design Upload), reads the
// file content as ArrayBuffer or text, and dispatches it to the
// browser adapter. The component does NOT implement any calculation
// rules — the adapter calls the existing core engine.

import React, { useState } from "react";
import { Upload, Button, message, Alert } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";

import { runFile, type RunFileOutput } from "../adapters/runFile.js";
import { UploadFormatHelp } from "./UploadFormatHelp.js";

const { Dragger } = Upload;

export interface FileUploadProps {
  readonly onResult: (output: RunFileOutput, fileName: string) => void;
  readonly disabled?: boolean;
}

export function FileUpload({ onResult, disabled }: FileUploadProps): JSX.Element {
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File): Promise<boolean> => {
    setBusy(true);
    try {
      const ext = file.name.toLowerCase();
      const isXlsx = ext.endsWith(".xlsx") || ext.endsWith(".xls");
      const data = isXlsx
        ? await file.arrayBuffer()
        : await file.text();
      const out = await runFile(file.name, data);
      onResult(out, file.name);
      if (out.errors.length === 0) {
        message.success(`已加载：${file.name}`);
      } else {
        message.warning(`已加载但有 ${out.errors.length} 处错误：${file.name}`);
      }
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      message.error(`无法解析 ${file.name}：${reason}`);
    } finally {
      setBusy(false);
    }
    // Tell AntD Upload we handled the file.
    return false;
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    accept: ".json,.csv,.xlsx,.xls",
    beforeUpload: (file) => {
      void handleFile(file);
      return false;
    },
    disabled: busy || disabled,
  };

  return (
    <div>
      <Dragger {...props} style={{ padding: 16 }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽文件至此区域</p>
        <p className="ant-upload-hint">
          支持 .json / .csv / .xlsx 三种格式。文件由浏览器本地解析并直接调用核心引擎，
          不会上传到任何服务器。
        </p>
      </Dragger>
      <div style={{ marginTop: 8 }}>
        <UploadFormatHelp />
      </div>
      {busy && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="正在解析并执行测算…"
        />
      )}
      <div style={{ marginTop: 12 }}>
        <Button
          type="default"
          onClick={() => {
            // Programmatic demo: synthesize a new-supply input as JSON.
            // This is purely a convenience for smoke-testing; in
            // production users upload their own files.
            const sample = JSON.stringify({
              calculationType: "new-supply",
              input: {
                parcel: {
                  projectName: "示例项目（WebUI 演示）",
                  landArea: 10000,
                  supplyMethod: "招拍挂",
                  calculationType: "新供应土地",
                  calculationDate: "2026-06-17",
                  mainLandUse: "住宅",
                  projectType: "普通项目",
                  totalGfa: 30000,
                },
                prices: {
                  residential: 25000,
                },
                items: [
                  {
                    id: "demo-1",
                    buildingType: "住宅",
                    gfa: 30000,
                    landTermYears: 70,
                    propertyCondition: "允许分割转让",
                    isGovernmentOwned: false,
                    isUnderground: false,
                    applicablePriceType: "住宅",
                  },
                ],
              },
            });
            const out = runFile("demo.json", sample);
            out.then((o) => {
              onResult(o, "demo.json");
              message.success("已加载内置示例");
            });
          }}
        >
          加载内置示例
        </Button>
      </div>
    </div>
  );
}
